<script setup lang="ts">
/**
 * TrustStripV3 — 3 claims honnêtes du hero V3 (sous la recherche ≥ lg, sous
 * « À la une » < lg).
 *
 * Un seul DOM responsive : liste empilée < md, 3 colonnes ≥ md, filet haut.
 * Lisibilité senior (DESIGN.md parcours) : titre DM Sans 17 px medium, description
 * 16 px encre atténuée — plus de mono 9-11 px ni de variante par viewport.
 * Pas d'icônes, pas de fond coloré — l'honnêteté des claims porte le message.
 *
 * Claims figés en i18n :
 * - pages.home.trustV3.claims.noCommission
 * - pages.home.trustV3.claims.directContact
 * - 3e claim : DYNAMIQUE selon le type actif (switcher) —
 *   titre : pages.home.homeType.trustBadge.{all|cdh|gite}
 *   desc  : all/gite → pages.home.homeType.trustBadgeDesc.{all|gite}
 *           cdh      → pages.home.trustV3.claims.breakfast.desc (réutilisée)
 *   (« Deux expériences » / « Petit-déjeuner inclus » / « Maison entière »).
 *
 * `variant="fixed"` (sous le parcours en étapes de l'accueil) : les 2 claims
 * fixes seuls, empilés < md, 2 colonnes ≥ md (le 3e doublonnait le texte des
 * cartes de type).
 */

const props = withDefaults(defineProps<{
  variant?: 'full' | 'fixed'
}>(), {
  variant: 'full',
})

const { t } = useI18n()
// Le 3e claim suit le type actif, y compris « gîte » sans stock (le switcher
// propose désormais toujours l'option — alignement sur /search).
const { type } = useHomeType()

// Les 2 premiers claims sont communs à tout le site ; le 3e s'adapte au type.
const FIXED_CLAIM_KEYS = ['noCommission', 'directContact'] as const

// Liste unifiée des 3 claims (2 fixes + 1 dynamique selon le type) — source
// unique de la liste. Le 3e `desc` : CDH réutilise la description
// « breakfast » réglementaire ; all/gite ont leur propre desc dédiée.
const thirdClaimDesc = computed(() =>
  type.value === 'cdh'
    ? t('pages.home.trustV3.claims.breakfast.desc')
    : t(`pages.home.homeType.trustBadgeDesc.${type.value}`),
)

const claims = computed(() => {
  const fixed = FIXED_CLAIM_KEYS.map(key => ({
    title: t(`pages.home.trustV3.claims.${key}.title`),
    desc: t(`pages.home.trustV3.claims.${key}.desc`),
  }))
  if (props.variant === 'fixed') return fixed
  return [
    ...fixed,
    {
      title: t(`pages.home.homeType.trustBadge.${type.value}`),
      desc: thirdClaimDesc.value,
    },
  ]
})
</script>

<template>
  <ul
    class="w-full border-t border-paper-200 pt-5 md:pt-6 grid gap-4"
    :class="variant === 'fixed' ? 'md:grid-cols-2 md:gap-6' : 'md:grid-cols-3 md:gap-6'"
    :data-variant="variant === 'fixed' ? 'fixed' : undefined"
  >
    <li
      v-for="(claim, idx) in claims"
      :key="idx"
    >
      <p class="font-sans text-[17px] font-medium leading-snug text-paper-900">
        {{ claim.title }}
      </p>
      <p
        v-if="claim.desc"
        class="mt-1 font-sans text-base text-paper-900/75 leading-[1.45]"
      >
        {{ claim.desc }}
      </p>
    </li>
  </ul>
</template>
